import {AppState} from '../../store/AppState'
import {
  changeCalendar,
  changeDay,
  changeHour,
  changeIsLeapMonth,
  changeMinute,
  changeMonth,
  changeSex,
  changeYear,
} from '../../store/owner/actions'
import {EumYang} from '../../enums/EumYang'
import {Dispatch} from 'redux'
import {connect} from 'react-redux'
import {getDaysOfLunarMonth, getSolarDateFromLunarDate} from '../../functions/lunar/utils'
import OwnerInfoBox from './input-box-container'
import moment, {Moment} from "moment-timezone";
import {Ji} from "../../enums/Ji";
import {Gan} from "../../enums/Gan";

const mapStateToProps = ({owner}: AppState) => ({
  owner: owner,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onSexChanged: (sex: EumYang) => dispatch(changeSex(sex)),
  onCalendarChanged: (calendar: EumYang) => dispatch(changeCalendar(calendar)),
  onYearChanged: (year: string) => dispatch(changeYear(year)),
  onMonthChanged: (month: string) => dispatch(changeMonth(month)),
  onDayChanged: (day: string) => dispatch(changeDay(day)),
  onHourChanged: (hour: string) => dispatch(changeHour(hour)),
  onMinuteChanged: (minute: string) => dispatch(changeMinute(minute)),
  onIsLeapMonthChanged: (isLeapMonth: boolean) =>
    dispatch(changeIsLeapMonth(isLeapMonth)),
})

export default connect(mapStateToProps, mapDispatchToProps)(OwnerInfoBox)

export function checkYear(input: string): boolean {
  const num = parseInt(input)
  if (isNaN(num) || num < 1901 || 2099 < num) return false

  return true
}

export function checkMonth(input: string): boolean {
  const num = parseInt(input)
  if (isNaN(num) || num < 1 || 12 < num) return false

  return true
}

export function checkDay(
  yearString: string,
  monthString: string,
  dayString: string,
  calendar: EumYang,
  isLeapMonth: boolean,
): boolean {
  if (
    dayString.trim() === '' ||
    yearString.trim() === '' ||
    monthString.trim() === ''
  )
    return true // 빈 문자열일 경우 무조건 트루를 리턴한다.
  const year = parseInt(yearString)
  const day = parseInt(dayString)
  const month = parseInt(monthString)
  if (
    isNaN(day) ||
    day < 1 ||
    getDaysOfMonth(year, month, calendar, isLeapMonth) < day
  ) {
    return false
  }
  return true
}

export function checkHour(input: string): boolean {
  const num = parseInt(input)
  if (isNaN(num) || num < 0 || 23 < num) return false

  return true
}

export function checkMinute(input: string): boolean {
  const num = parseInt(input)
  if (isNaN(num) || num < 0 || 59 < num) return false

  return true
}

function getDaysOfMonth(
  year: number,
  month: number,
  calendar: EumYang,
  isLeapMonth: boolean,
): number {
  if (calendar === EumYang.YANG) {
    if ([1, 3, 5, 7, 8, 10, 12].includes(month)) {
      return 31
    }
    if (month === 2 && year % 4 === 0) {
      return 29
    }
    if (month === 2) {
      return 28
    }
  } else {
    return getDaysOfLunarMonth(year, month, isLeapMonth)
  }
  return 30
}

// 써머타임에 해당하는 기간인지 확인
export function checkSummerTime(originYear: string, originMonth: string, originDay: string, hour: string, minute: string, calendar: EumYang, leapMonth: boolean) {
  const targetTime = getMomentFromDiscretTimeInput(originYear, originMonth, originDay, hour, minute, calendar, leapMonth);
  if (targetTime)
    return targetTime.isDST();
  return false;
}

// 자시, 축시, 인시 등 시간 표시
export function getOrientalTime(originYear: string, originMonth: string, originDay: string, hour: string, minute: string, calendar: EumYang, leapMonth: boolean): string {
  const targetTime = getMomentFromDiscretTimeInput(originYear, originMonth, originDay, hour, minute, calendar, leapMonth)
  if (targetTime) {
    return getSiJiString(targetTime)

  }
  return "";
}

function getMomentFromDiscretTimeInput(originYear: string, originMonth: string, originDay: string, hour: string, minute: string, calendar: EumYang, leapMonth: boolean): Moment | undefined {
  if (checkYear(originYear) && checkMonth(originMonth) && checkDay(originYear, originMonth, originDay, calendar, leapMonth)) {
    let year = originYear;
    let month = originMonth;
    let day = originDay;

    if (calendar === EumYang.EUM) {
      // originYear 가 올바른 값이 아닐 경우 여기서 undefined 오류 발생하고
      const solarDate = getSolarDateFromLunarDate({
        year: originYear,
        month: originMonth,
        day: originDay,
        leapMonth: leapMonth
      });
      year = solarDate.year;
      month = solarDate.month;
      day = solarDate.day;
    }
    // 여기서는 warning 발생.
    const targetTime = moment.tz(`${year}-${toTwoDigit(month)}-${toTwoDigit(day)} ${toTwoDigit(hour)}:${toTwoDigit(minute)}`, "Asia/Seoul");
    return targetTime;
  }
}

// 7시로 입력하면 07시로 바꿔준다.
function toTwoDigit(input: string) {

  const inputNumber: number = parseInt(input);

  if (inputNumber < 10) {
    return `0${inputNumber}`;
  } else {
    return input;
  }

}

const TWO_HOUR_IN_MILLI = 7200000;
const BASE_TIME = moment.tz("1900-02-03 23:30", "Asia/Tokyo"); // 무신일 // 임자시

function getSiJiString(target: moment.Moment) {
  const elapse = Math.floor((target.toDate().getTime() - BASE_TIME.toDate().getTime()) / TWO_HOUR_IN_MILLI);
  const jiMod = elapse % 12;

  let ji = jiMod + Ji.JA;
  if (ji > Ji.HAE) ji -= 12;

  return jiToString(ji);
}

function jiToString(ji: Ji): string {
  switch (ji) {
    case Ji.JA:
      return "자시"
    case Ji.CHUK:
      return "축시"
    case Ji.IN:
      return "인시"
    case Ji.MYO:
      return "묘시"
    case Ji.JIN:
      return "진시"
    case Ji.SA:
      return "사시"
    case Ji.OH:
      return "오시"
    case Ji.MI:
      return "미시"
    case Ji.SIN:
      return "신시"
    case Ji.YU:
      return "유시"
    case Ji.SUL:
      return "술시"
    case Ji.HAE:
      return "해시"
    default:
      return "";

  }
}